import { cn } from "@/utils/utils";
import { SxProps, Theme, useMediaQuery } from "@mui/material";
import { format } from "date-fns";
import { MouseEvent, ReactNode, useState } from "react";
import {
  DateRange,
  DayPicker,
  SelectRangeEventHandler,
} from "react-day-picker";
import Calendar from "../ui/calendar";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  anchor?: ReactNode;
  date?: DateRange;
  handleOpenUserMenu?: (event: MouseEvent<HTMLElement>) => void;
  handleCloseUserMenu: () => void;
  handleConform: () => void;
  handleSelect: SelectRangeEventHandler;
  anchorElUser: null | HTMLElement;
  styleMenu?: SxProps<Theme>;
  setDate: (date: DateRange | undefined) => void;
  classNamePlaceholder?: string;
  numberOfMonths?: number;
  contentStyle?: React.CSSProperties;
};

const SelectDate = ({
  date,
  handleSelect,
  setDate,
  classNamePlaceholder,
  numberOfMonths = 2,
  contentStyle,
  ...props
}: CalendarProps) => {
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className="w-full">
        <div className="flex h-12 w-full flex-1 cursor-pointer items-center justify-between rounded-lg border border-[#E2E8F0] bg-white px-3 pr-2 ">
          <div className="flex items-center gap-3">
            {!classNamePlaceholder && (
              <img src="/icons/calendar.svg" alt="calendar" />
            )}
            <p
              className={cn(
                "text-sm font-normal text-[#94A3B8]",
                classNamePlaceholder,
              )}
            >
              {!date?.from && !date?.to ? (
                classNamePlaceholder ? (
                  "Select event duration"
                ) : (
                  "Select date"
                )
              ) : (
                <p className="w-full text-text">
                  {date?.from ? format(date.from, "yyyy/MM/dd") : " "} -{" "}
                  {date?.to ? format(date.to, "yyyy/MM/dd") : " "}
                </p>
              )}
            </p>
          </div>
          <KeyboardArrowDownIcon
            className=""
            sx={{
              color: "#94A3B8",
            }}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-full"
        side="bottom"
        align={isMobile ? "end" : "start"}
        style={contentStyle}
      >
        <Calendar
          {...props}
          mode="range"
          defaultMonth={date?.from}
          numberOfMonths={numberOfMonths}
          selected={date}
          onSelect={handleSelect}
          initialFocus
        />
        <div className="mt-3 flex w-full justify-end gap-2 px-2">
          <button
            onClick={() => {
              setDate(undefined);
              setOpen(false);
            }}
            className="rounded-lg bg-button-background text-sm font-medium text-text max-md:h-8 max-md:w-[98px] lg:px-3 lg:py-2 lg:text-[16px]"
          >
            Reset Date
          </button>
          <button
            onClick={() => setOpen(false)}
            className="rounded-lg bg-text text-sm font-medium text-white max-md:h-8 max-md:w-[63px] lg:px-3 lg:py-2 lg:text-[16px]"
          >
            Apply
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SelectDate;
